import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
        registerPushForCurrentUser,
        unregisterPushOnLogout
} from "../../services/webPush";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
        let refreshInFlight = null;

        const clearSession = () => {
                localStorage.removeItem("token");
                api.defaults.headers.Authorization = undefined;
                setIsAuth(false);
                setUser({});
        };

        const requestInterceptor = api.interceptors.request.use(
                config => {
                        const stored = localStorage.getItem("token");

                        if (stored) {
                                try {
                                        const token = JSON.parse(stored);

                                        if (typeof token === "string" && token) {
                                                config.headers = config.headers || {};
                                                config.headers.Authorization = `Bearer ${token}`;
                                        }
                                } catch (_) {
                                        localStorage.removeItem("token");
                                }
                        }

                        return config;
                }
        );

        const responseInterceptor = api.interceptors.response.use(
                response => response,
                async error => {
                        const originalRequest = error.config;
                        const status = error?.response?.status;
                        const message = error?.response?.data?.error;

                        const isRefresh =
                                originalRequest?.url?.includes("/auth/refresh_token");

                        const expiredAccessToken =
                                status === 403 &&
                                typeof message === "string" &&
                                message.startsWith("Invalid token.");

                        if (
                                expiredAccessToken &&
                                originalRequest &&
                                !originalRequest._retry &&
                                !isRefresh
                        ) {
                                originalRequest._retry = true;

                                try {
                                        if (!refreshInFlight) {
                                                refreshInFlight = api
                                                        .post("/auth/refresh_token")
                                                        .then(({ data }) => {
                                                                if (!data?.token) {
                                                                        throw new Error("REFRESH_WITHOUT_TOKEN");
                                                                }

                                                                localStorage.setItem(
                                                                        "token",
                                                                        JSON.stringify(data.token)
                                                                );

                                                                api.defaults.headers.Authorization =
                                                                        `Bearer ${data.token}`;

                                                                return data.token;
                                                        })
                                                        .finally(() => {
                                                                refreshInFlight = null;
                                                        });
                                        }

                                        const token = await refreshInFlight;

                                        originalRequest.headers =
                                                originalRequest.headers || {};

                                        originalRequest.headers.Authorization =
                                                `Bearer ${token}`;

                                        return api(originalRequest);
                                } catch (refreshError) {
                                        if (
                                                [401, 403].includes(
                                                        refreshError?.response?.status
                                                )
                                        ) {
                                                clearSession();
                                        }

                                        return Promise.reject(refreshError);
                                }
                        }

                        if (
                                (isRefresh && [401, 403].includes(status)) ||
                                (status === 401 && message === "ERR_SESSION_EXPIRED")
                        ) {
                                clearSession();
                        }

                        return Promise.reject(error);
                }
        );

        return () => {
                api.interceptors.request.eject(requestInterceptor);
                api.interceptors.response.eject(responseInterceptor);
        };
}, []);

useEffect(() => {
		const token = localStorage.getItem("token");
		void (async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					localStorage.setItem("token", JSON.stringify(data.token));
                                        api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);


        useEffect(() => {
                if (!isAuth || !user?.id) return undefined;

                const registerDevice = () => {
                        if (document.visibilityState !== "visible") return;

                        registerPushForCurrentUser(user.id).catch(() => {
                                console.warn(
                                        "Web Push: registro no disponible"
                                );
                        });
                };

                registerDevice();

                const renewalTimer = window.setInterval(
                        registerDevice,
                        12 * 60 * 60 * 1000
                );

                document.addEventListener("visibilitychange", registerDevice);
                window.addEventListener("focus", registerDevice);

                return () => {
                        window.clearInterval(renewalTimer);
                        document.removeEventListener(
                                "visibilitychange", registerDevice
                        );
                        window.removeEventListener("focus", registerDevice);
                };
        }, [isAuth, user?.id]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
                        let pushEndpoint;

                        try {
                                pushEndpoint = await unregisterPushOnLogout();
                        } catch (_) {
                                console.warn("Web Push: cancelacion local pendiente");
                        }

			await api.delete("/auth/logout", {
                                data: { pushEndpoint }
                        });
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
